import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import "./image-preview-modal.css";

/**
 * A reusable Image Preview Modal component.
 * 
 * @param {boolean} show - Whether the modal is visible.
 * @param {string} src - The source URL of the image to preview (fallback if images not provided).
 * @param {Array<string>} images - Array of image source URLs for gallery mode.
 * @param {number} initialIndex - Starting index in the images array.
 * @param {function} onHide - Callback function to close the modal.
 * @param {string} title - Optional title for the modal (defaults to "Attachment Preview").
 */
const ImagePreviewModal = ({ show, src, images = [], initialIndex = 0, onHide, title = "Attachment Preview" }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (show) {
      setCurrentIndex(initialIndex);
    }
  }, [show, initialIndex]);

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const currentImage = images.length > 0 ? images[currentIndex] : src;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      size="lg" 
      className="common-image-preview-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0 shadow-none">
        <Modal.Title className="preview-modal-title">
          {title} {images.length > 1 && `(${currentIndex + 1}/${images.length})`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex justify-content-center p-3 position-relative">
        {images.length > 1 && (
          <button 
            className="preview-nav-btn prev" 
            onClick={handlePrev}
            aria-label="Previous image"
          >
            &#10094;
          </button>
        )}
        
        <img 
          src={currentImage} 
          alt={`Preview ${currentIndex + 1}`} 
          className="preview-image-content" 
        />

        {images.length > 1 && (
          <button 
            className="preview-nav-btn next" 
            onClick={handleNext}
            aria-label="Next image"
          >
            &#10095;
          </button>
        )}
      </Modal.Body>
    </Modal>
  );
};

ImagePreviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  src: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.string),
  initialIndex: PropTypes.number,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default ImagePreviewModal;
